// Réseau Social - Feed principal
// HARMONISÉ avec le design "Mon Espace"
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Image, Send, Users, UserPlus, Newspaper, Plus, Menu } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import CockpitParticulierBottomNav from '../../../components/layout/CockpitParticulierBottomNav';

function PostCard({ post, onLike, onComment, onShare, onSave }) {
  const [showComments, setShowComments] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] rounded-full flex items-center justify-center text-white font-bold">
          {post.author.name.charAt(0)}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{post.author.name}</p>
          <p className="text-xs text-gray-500">{post.date}</p>
        </div>
        <button className="p-2 hover:bg-gray-100 rounded-full">
          <MoreHorizontal size={18} className="text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-gray-800">{post.content}</p>
      </div>

      {/* Image */}
      {post.image && (
        <div className="bg-gray-100">
          <img src={post.image} alt="" className="w-full max-h-96 object-cover" />
        </div>
      )}

      {/* Stats */}
      <div className="px-4 py-2 flex items-center justify-between text-sm text-gray-500 border-b border-gray-100">
        <span>{post.likes} j'aime</span>
        <span>{post.comments} commentaires • {post.shares} partages</span>
      </div>

      {/* Actions */}
      <div className="px-2 py-1 flex items-center">
        <button 
          onClick={() => onLike(post.id)}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
            post.liked ? 'text-red-500' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Heart size={18} className={post.liked ? 'fill-red-500' : ''} />
          <span className="text-sm">J'aime</span>
        </button>
        <button 
          onClick={() => setShowComments(!showComments)}
          className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <MessageCircle size={18} />
          <span className="text-sm">Commenter</span>
        </button>
        <button 
          onClick={() => onShare(post.id)}
          className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Share2 size={18} />
          <span className="text-sm">Partager</span>
        </button>
        <button 
          onClick={() => onSave(post.id)}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${
            post.saved ? 'text-[#1E3A5F]' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Bookmark size={18} className={post.saved ? 'fill-[#1E3A5F]' : ''} />
          <span className="text-sm">Sauvegarder</span>
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
          <div className="flex gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              JD
            </div>
            <input
              type="text"
              placeholder="Écrire un commentaire..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-full text-sm focus:ring-2 focus:ring-[#1E3A5F] focus:border-[#1E3A5F]/500"
            />
            <button className="p-2 bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white rounded-full">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function FeedReseau({ onOpenMenu }) {
  const { user } = useCurrentUser();
  
  // Posts démo
  const DEMO_POSTS = [];

  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');

  useEffect(() => {
    if (user?.isDemo) {
      setPosts(DEMO_POSTS);
    } else {
      setPosts([]);
    }
  }, [user]);

  const handleLike = (id) => {
    setPosts(prev => prev.map(p => 
      p.id === id ? { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 } : p
    ));
  };

  const handleSave = (id) => {
    setPosts(prev => prev.map(p => 
      p.id === id ? { ...p, saved: !p.saved } : p
    ));
  };

  const handleShare = (id) => {
    console.log('Partager:', id);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style identique à Mon Espace */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            {/* Menu Hamburger */}
            {onOpenMenu && (
              <button
                onClick={onOpenMenu}
                className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="hamburger-menu"
              >
                <Menu size={22} className="text-[#1E3A5F]" />
              </button>
            )}
            <div>
              <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Réseau Social</h1>
              <p className="text-sm text-gray-500">Découvre les publications de ta communauté</p>
            </div>
          </div>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm">
            <Plus size={16} />
            <span className="hidden sm:inline">Nouvelle publication</span>
          </Button>
        </div>
      </div>

      {/* Créer un post */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1">
            <textarea
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="Quoi de neuf ?"
              className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              rows={3}
            />
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                  <Image size={18} />
                </button>
              </div>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm h-8 px-4">
                Publier
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Feed */}
      <div className="flex-1 overflow-y-auto space-y-3">
        {posts.length > 0 ? (
          posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onLike={handleLike}
              onComment={() => {}}
              onShare={handleShare}
              onSave={handleSave}
            />
          ))
        ) : (
          /* État vide - Style harmonisé */
          <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
            <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4">
              <Newspaper size={24} className="text-orange-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Ton feed est vide</h3>
            <p className="text-sm text-gray-500 mb-4 max-w-xs mx-auto">
              Abonne-toi à des utilisateurs ou publie ton premier post pour commencer !
            </p>
            <Link to="/reseau">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl">
                Explorer le réseau
              </Button>
            </Link>
          </div>
        )}
      </div>
      
      {/* Navbar fixe en bas - Mobile uniquement */}
      <CockpitParticulierBottomNav />
    </div>
  );
}
